import {
  Component,
  ElementRef,
  EventEmitter,
  inject, Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { debounceTime, exhaustMap, filter, finalize, map, of, Subscription, tap } from 'rxjs';
import { IFriend, IFriendsResponse } from '../../../friends/model/friends.model';
import { FriendsCrudService } from '../../../friends/service/friends-crud.service';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MIN_QUERY_LENGTH } from 'app/friends/friends.const';
import { MatTooltip } from '@angular/material/tooltip';
import { ShareFriendCardComponent } from 'app/shared/share-friend-card/share-friend-card.component';
import { IconComponent } from 'app/shared/icon/icon.component';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgIcon, provideIcons } from "@ng-icons/core";
import { heroInformationCircle } from "@ng-icons/heroicons/outline";
import { IIclData } from "../story";

@Component({
  selector: 'app-share-story',
  standalone: true,
  imports: [
    MatTooltip,
    MatSlideToggleModule,
    ShareFriendCardComponent,
    IconComponent,
    ReactiveFormsModule,
    NgIcon,
  ],
  providers: [
    provideIcons({ heroInformationCircle }),
  ],
  templateUrl: './share-story.component.html',
  styleUrls: ['./share-story.component.scss'],
})
export class ShareStoryComponent implements OnInit, OnDestroy {
  @Input() acl!: IIclData;
  @Output() shareWith = new EventEmitter<IIclData>();
  @ViewChild('scrollFriendsContainer', { static: false }) scrollFriendsContainer!: ElementRef;

  public search = new FormControl('', [Validators.minLength(MIN_QUERY_LENGTH)]);
  public shareWithAll = new FormControl(true);
  public shareWithAcquaintances = new FormControl(false);

  friendsList = signal<IFriend[]>([]);
  selectedFriends = signal(new Set<number>());

  private friendsCrudService = inject(FriendsCrudService);
  private subscriptions = new Subscription();

  private isFriendsFetching = false;
  private allFriendsLoaded = false;

  ngOnInit() {
    if (this.acl) {
      this.shareWithAcquaintances.setValue(this.acl.acquaintance)

      if (this.acl.friends?.length)  {
        this.selectedFriends.set(new Set(this.acl.friends));
      }

      if (this.acl.friends === null || this.acl.friends?.length) {
        this.shareWithAll.setValue(false);
        this.allFriendsLoaded = false;
        this.getInitialFriends();
      }

      if (this.acl.friends?.length === 0) {
        this.shareWithAll.setValue(true);
      }

      if (this.shareWithAll.value === false && !this.selectedFriends().size) {
        this.shareWithAcquaintances.disable()
      }
    }

    this.subscriptions.add(
      this.search.valueChanges
        .pipe(
          filter((value): value is string => value !== null),
          map((value: string) => value.trim().toLowerCase()),
          debounceTime(500),
          filter((value: string) => value.length >= 1 || !value.length),
        )
        .subscribe((query) => {
          // Reset friends list before filtering
          this.friendsList.set([]);
          this.allFriendsLoaded = false;

          this.loadMoreFriends(query);
        }),
    );

    this.subscriptions.add(
      this.shareWithAll.valueChanges.subscribe((shareWithAll) => {
        this.selectedFriends.set(new Set());

        if (!shareWithAll) {
          // Reset friends list before filtering
          this.friendsList.set([]);
          this.shareWithAcquaintances.setValue(false)
          this.allFriendsLoaded = false;
          this.getInitialFriends();
        }

        this.updateAcl();
      }),
    );

    this.subscriptions.add(
      this.shareWithAcquaintances.valueChanges.subscribe(() => {
        this.updateAcl();
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onFriendsScroll() {
    const container = this.scrollFriendsContainer.nativeElement;
    const bottomReached = container.scrollTop + container.clientHeight >= container.scrollHeight - 200;

    if (bottomReached && !this.allFriendsLoaded && !this.isFriendsFetching) {
      this.loadMoreFriends(this.search.value || '');
    }
  }

  getInitialFriends() {
    this.subscriptions.add(
      this.friendsCrudService
        .getFriends('', 50)
        .pipe(
          tap(() => (this.isFriendsFetching = true)),
          finalize(() => (this.isFriendsFetching = false)),
        )
        .subscribe((friends: IFriendsResponse) => {
          if (friends.data.length) {
            this.friendsList.set(this.updateFriendsList(friends.data));
            this.allFriendsLoaded = friends.total <= this.friendsList().length;
            this.friendsCrudService.setFriendsTotal(friends.total);
          }
        }),
    );
  }

  updateFriendsList(friends: IFriend[]) {
    const selectedFriends = this.selectedFriends();
    return friends.map((friend) => {
      return { ...friend, selected: selectedFriends.has(friend.id) };
    });
  }

  private loadMoreFriends(query: string): void {
    if (this.allFriendsLoaded || this.isFriendsFetching) return;

    this.isFriendsFetching = true;

    this.friendsCrudService
      .getFriends(query, 50, this.friendsList().length)
      .pipe(
        exhaustMap((friendsResponse) => {
          if (friendsResponse.data.length) {
            this.friendsList.update((list) => [...list, ...this.updateFriendsList(friendsResponse.data)]);
            this.allFriendsLoaded = friendsResponse.total <= this.friendsList().length;
            this.friendsCrudService.setFriendsTotal(friendsResponse.total);
          }
          return of(null);
        }),
        finalize(() => (this.isFriendsFetching = false)),
      )
      .subscribe();
  }

  updateAcl() {
    const selectedFriends = this.selectedFriends();

    if (!selectedFriends.size && !this.shareWithAll.value && this.shareWithAcquaintances.value === true) {
      this.shareWithAcquaintances.setValue(false)
    }

    let friends: number[] | null = Array.from(selectedFriends)

    if (!this.shareWithAll.value && !selectedFriends.size) {
      friends = null;
    }

    this.shareWith.emit({
      friends,
      acquaintance: !!this.shareWithAcquaintances.value,
      communities: null
    })
  }

  changedFriendSelection(event: { id: number; select: boolean }) {
    let friendsList;
    const selectedFriends = this.selectedFriends();

    if (event.select) {
      selectedFriends.add(event.id);
      friendsList = this.friendsList().map((friend: IFriend) => {
        if (friend.id == event.id) {
          return { ...friend, selected: event.select };
        }
        return friend;
      });

      this.friendsList.set(friendsList);
    } else {
      selectedFriends.delete(event.id);
    }

    this.updateAcl();
  }
}
